/**
 * Ограничивает Ctrl/⌘ + колесо и Ctrl/⌘ +/− масштаб страницы до 50–150%.
 * Браузерный zoom через меню ОС полностью не блокируется — перехватываем типичные
 * жесты и ведём свой CSS zoom в допустимом диапазоне.
 *
 * Важно: при CSS zoom на <html> единицы 100vw/100vh/100dvh расходятся с visual
 * viewport. Fixed-оверлеи (ЛП/RSD) должны опираться на inset:0 без vw/vh;
 * координаты из getBoundingClientRect нужно делить на getPageZoom().
 *
 * Zoom > 1: html/width:100% × zoom визуально шире окна. Карточку спасает сжатие
 * html до innerWidth/zoom. Fixed-шапка считает % от viewport и при zoom на html
 * calc() часто игнорируется — ширину шапки задаём в px явно.
 * Ctrl/⌘+0 сбрасывает. После логина вызвать syncPageZoomLayout() — шапки
 * ещё нет в DOM на initPageZoomLimit.
 */

const ZOOM_MIN = 0.5
const ZOOM_MAX = 1.5
const ZOOM_STEP = 0.1
const STORAGE_KEY = 'ltms.pageZoom'
export const PAGE_ZOOM_EVENT = 'ltms:page-zoom'

function clampZoom(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 1
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n * 100) / 100))
}

function readStoredZoom() {
  try {
    return clampZoom(localStorage.getItem(STORAGE_KEY) || 1)
  } catch {
    return 1
  }
}

/** Текущий CSS zoom страницы (1 = 100%). */
export function getPageZoom() {
  const raw = document.documentElement.style.zoom
  if (!raw) return 1
  return clampZoom(raw)
}

/**
 * Переводит visual/client-координаты (getBoundingClientRect, visualViewport)
 * в CSS-пиксели до применения zoom — для style.top / CSS-переменных.
 */
export function cssPxFromVisual(visualPx) {
  const zoom = getPageZoom()
  if (!zoom || zoom === 1) return visualPx
  return visualPx / zoom
}

/**
 * DOMRect / getBoundingClientRect → CSS-пиксели до zoom (для fixed top/left/width).
 *
 * @param {DOMRect | { top: number, left: number, bottom: number, right: number, width: number, height: number }} rect
 */
export function cssRectFromVisual(rect) {
  const zoom = getPageZoom()
  if (!zoom || zoom === 1) {
    return {
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    }
  }
  return {
    top: rect.top / zoom,
    left: rect.left / zoom,
    bottom: rect.bottom / zoom,
    right: rect.right / zoom,
    width: rect.width / zoom,
    height: rect.height / zoom,
  }
}

/** Ширина окна в CSS px до применения нашего zoom. */
function layoutViewportWidth() {
  return window.innerWidth || document.documentElement.clientWidth || 0
}

/**
 * Размер visual viewport в CSS px до page zoom.
 * Согласован с cssRectFromVisual / style.left|width fixed-оверлеев.
 *
 * @returns {{ width: number, height: number }}
 */
export function cssViewportSize() {
  const zoom = getPageZoom() || 1
  return {
    width: (window.innerWidth || document.documentElement.clientWidth || 0) / zoom,
    height: (window.innerHeight || document.documentElement.clientHeight || 0) / zoom,
  }
}

/**
 * Fixed-шапка: ширина в px = layout viewport / zoom.
 * CSS calc(100%/zoom) при zoom на html в Chromium/Яндексе не удерживает край.
 *
 * @param {number} zoom
 * @param {number} layoutW
 */
function syncFixedNavbarWidth(zoom, layoutW) {
  const navbar = document.querySelector('.navbar.navbar-dark')
  if (!navbar) {
    return
  }
  if (zoom === 1 || !(layoutW > 0)) {
    navbar.style.removeProperty('width')
    navbar.style.removeProperty('max-width')
    return
  }
  navbar.style.width = `${layoutW}px`
  navbar.style.maxWidth = `${layoutW}px`
}

/**
 * Подгоняет layout-ширину html и fixed-шапки под CSS zoom.
 *
 * @param {HTMLElement} root
 * @param {number} zoom
 */
function applyZoomLayout(root, zoom) {
  root.style.setProperty('--ltms-page-zoom', String(zoom))

  if (zoom === 1) {
    root.style.removeProperty('width')
    root.style.removeProperty('max-width')
    root.style.removeProperty('overflow-x')
    document.body.style.removeProperty('overflow-x')
    document.body.style.removeProperty('max-width')
    syncFixedNavbarWidth(1, 0)
    return
  }

  const viewportW = layoutViewportWidth()
  const layoutW = viewportW > 0 ? viewportW / zoom : 0
  if (layoutW > 0) {
    root.style.width = `${layoutW}px`
    root.style.maxWidth = `${layoutW}px`
  }
  syncFixedNavbarWidth(zoom, layoutW)
  root.style.overflowX = 'auto'
  document.body.style.maxWidth = '100%'
  document.body.style.overflowX = 'auto'
}

/**
 * Повторно применить ширины html/шапки к текущему zoom.
 * Нужно после mount signed-layout (шапки нет на экране логина).
 */
export function syncPageZoomLayout() {
  applyZoomLayout(document.documentElement, getPageZoom())
}

function applyZoom(value) {
  const zoom = clampZoom(value)
  const root = document.documentElement
  if (zoom === 1) {
    root.style.removeProperty('zoom')
  } else {
    root.style.zoom = String(zoom)
  }
  applyZoomLayout(root, zoom)
  try {
    localStorage.setItem(STORAGE_KEY, String(zoom))
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(
    new CustomEvent(PAGE_ZOOM_EVENT, { detail: { zoom } })
  )
  return zoom
}

function currentZoom() {
  return getPageZoom()
}

function onWheel(event) {
  if (!(event.ctrlKey || event.metaKey)) return
  event.preventDefault()
  const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
  applyZoom(currentZoom() + delta)
}

function onKeyDown(event) {
  if (!(event.ctrlKey || event.metaKey)) return
  const key = event.key
  if (key === '+' || key === '=') {
    event.preventDefault()
    applyZoom(currentZoom() + ZOOM_STEP)
    return
  }
  if (key === '-' || key === '_') {
    event.preventDefault()
    applyZoom(currentZoom() - ZOOM_STEP)
    return
  }
  if (key === '0') {
    event.preventDefault()
    applyZoom(1)
  }
}

function onViewportResize() {
  syncPageZoomLayout()
}

export function initPageZoomLimit() {
  applyZoom(readStoredZoom())
  window.addEventListener('wheel', onWheel, { passive: false })
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', onViewportResize)
  window.visualViewport?.addEventListener('resize', onViewportResize)
}
